import { ApiError } from "@lumora/bridge";
import { ViewModel } from "../../mvvm/ViewModel.js";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PURPOSE = "slug_claim";
function mapError(err, fallback) {
    if (err instanceof ApiError) {
        if (err.status === 429 || err.code === "rate_limited")
            return "Too many attempts. Wait a minute, then try again.";
        if (err.code === "bot_suspected")
            return "We could not accept that request. Reload the page and try again.";
        if (err.status === 503)
            return "Claiming is not available right now. Try again later.";
        if (err.status === 400 || err.status === 403)
            return err.message || fallback;
    }
    return fallback;
}
/** Email, code, slug availability, then claim (#1412). */
export class DevClaimViewModel extends ViewModel {
    client;
    honeypotField;
    site;
    debounceMs;
    minLen;
    timer = null;
    seq = 0;
    constructor(client, opts = {}) {
        super({
            step: "email", email: "", code: "", slug: "", honeypot: "", verificationId: null,
            slugStatus: "idle", slugMessage: null, loading: false, error: null, claimToken: null, claimedSlug: null,
        });
        this.client = client;
        this.site = opts.site ?? "lumora";
        this.debounceMs = opts.debounceMs ?? 400;
        this.minLen = opts.minSlugLength ?? 3;
        this.honeypotField = opts.honeypotField ?? "website";
    }
    setEmail(email) { this.setState({ email: email.trim(), error: null }); }
    setCode(code) { this.setState({ code: code.replace(/\s+/g, ""), error: null }); }
    setHoneypot(value) { this.setState({ honeypot: value }); }
    /** Back to the email step (wrong address). */
    changeEmail() {
        this.cancelCheck();
        this.setState({ step: "email", code: "", verificationId: null, error: null });
    }
    async sendCode() {
        const { email } = this.state;
        if (!EMAIL_RE.test(email)) {
            this.setState({ error: "Enter a valid email address." });
            return;
        }
        this.setState({ loading: true, error: null });
        try {
            const res = (await this.client.startContactVerification(this.site, email, PURPOSE));
            this.setState({ loading: false, step: "code", verificationId: res?.verificationId ?? null, code: "" });
        }
        catch (err) {
            this.setState({ loading: false, error: mapError(err, "Could not send the code. Try again.") });
        }
    }
    async verify() {
        const { verificationId, code } = this.state;
        if (!verificationId || code.length < 4) {
            this.setState({ error: "Enter the code from your email." });
            return;
        }
        this.setState({ loading: true, error: null });
        try {
            await this.client.confirmContactVerification(this.site, verificationId, code);
            this.setState({ loading: false, step: "slug" });
        }
        catch (err) {
            this.setState({ loading: false, error: mapError(err, "That code did not work. Check it and try again.") });
        }
    }
    setSlug(raw) {
        const slug = raw.trim().toLowerCase();
        this.cancelCheck();
        if (slug.length < this.minLen) {
            this.setState({ slug, slugStatus: "idle", slugMessage: null, error: null });
            return;
        }
        this.setState({ slug, slugStatus: "checking", slugMessage: null, error: null });
        const mine = ++this.seq;
        this.timer = setTimeout(() => { this.timer = null; void this.check(slug, mine); }, this.debounceMs);
    }
    async check(slug, mine) {
        try {
            const res = await this.client.checkSlugAvailability(slug);
            if (mine !== this.seq)
                return;
            this.setState({ slugStatus: res.available ? "available" : "taken", slugMessage: null });
        }
        catch (err) {
            if (mine !== this.seq)
                return;
            if (err instanceof ApiError && err.status === 400) {
                this.setState({ slugStatus: "invalid", slugMessage: err.message || "That name cannot be used." });
            }
            else {
                this.setState({ slugStatus: "idle", error: mapError(err, "Could not check that name. Try again.") });
            }
        }
    }
    async claim() {
        const { slug, email, verificationId, honeypot, slugStatus } = this.state;
        if (slugStatus !== "available" || !verificationId)
            return;
        this.setState({ loading: true, error: null });
        try {
            const res = await this.client.claimSlug({ slug, email, verificationId, website: honeypot });
            this.setState({ loading: false, step: "done", claimToken: res.claimToken, claimedSlug: res.slug });
        }
        catch (err) {
            if (err instanceof ApiError && (err.status === 409 || err.status === 500)) {
                this.setState({ loading: false, slugStatus: "taken", error: "Someone just took that name. Pick another." });
                return;
            }
            this.setState({ loading: false, error: mapError(err, "Could not claim that name. Try again.") });
        }
    }
    dispose() { this.cancelCheck(); }
    cancelCheck() {
        this.seq++;
        if (this.timer)
            clearTimeout(this.timer);
        this.timer = null;
    }
}
//# sourceMappingURL=DevClaimViewModel.js.map